import axios from 'axios';
import { ClipboardList, Bell, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const GrilleEvaluationCentreSection = ({ stagiaireId, grilleEval, grilleStructure }) => {
  if (!grilleStructure) return null;

  const handleRappel = async () => {
    try {
      await axios.post(`/grille-evaluation/${stagiaireId}/rappel`);
      toast.success('Rappel envoyé au tuteur');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi du rappel");
    }
  };

  const hasEvaluations = grilleEval && Object.keys(grilleEval.evaluations || {}).length > 0;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="grille-evaluation-centre">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-amber-500" />
          Grille d'Évaluation Tuteur
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRappel}
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          data-testid="btn-rappel-grille"
        >
          <Bell className="h-4 w-4 mr-1" /> Rappeler le tuteur
        </Button>
      </div>

      {hasEvaluations ? (
        <div className="overflow-x-auto">
          <div className="flex flex-wrap items-center gap-2 pb-3 text-xs">
            <span className="px-2 py-1 bg-red-100 text-red-700 rounded">0 = Non intégré</span>
            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">1 = Début d'acquisition</span>
            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">2 = En cours</span>
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded">3 = Acquis</span>
          </div>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left p-2 border border-slate-200 min-w-[280px]">Critère</th>
                {grilleStructure.periodes.map(p => (
                  <th key={p.id} className="p-2 border border-slate-200 text-center min-w-[65px]">
                    <div className="text-xs font-bold">{p.mois}</div>
                    <div className="text-xs text-slate-500">{p.label}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grilleStructure.sections.map(section => (
                <SectionRows key={section.id} section={section} periodes={grilleStructure.periodes} evaluations={grilleEval.evaluations} />
              ))}
            </tbody>
          </table>
          {grilleEval.commentaire_global && (
            <div className="mt-3 p-3 bg-slate-50 rounded-lg border">
              <p className="text-xs font-medium text-slate-500 mb-1">Commentaire du tuteur :</p>
              <p className="text-sm text-slate-700">{grilleEval.commentaire_global}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 bg-slate-50 rounded-lg">
          <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Le tuteur n'a pas encore rempli la grille d'évaluation</p>
          <p className="text-xs text-slate-400 mt-1">Utilisez le bouton "Rappeler le tuteur" pour envoyer une notification</p>
        </div>
      )}
    </div>
  );
};

const SectionRows = ({ section, periodes, evaluations }) => (
  <>
    <tr>
      <td colSpan={periodes.length + 1} className="p-2 bg-amber-50 border border-slate-200 font-bold text-amber-800 text-xs uppercase tracking-wide">
        {section.titre}
      </td>
    </tr>
    {section.criteres.map((critere, idx) => {
      const key = `${section.id}_${idx}`;
      return (
        <tr key={key}>
          <td className="p-2 border border-slate-200 text-xs text-slate-700">{critere}</td>
          {periodes.map(p => {
            const val = evaluations?.[key]?.[p.id];
            const color = val === 0 ? 'bg-red-200 text-red-800' : val === 1 ? 'bg-orange-200 text-orange-800' : val === 2 ? 'bg-yellow-200 text-yellow-800' : val === 3 ? 'bg-green-200 text-green-800' : 'bg-slate-50 text-slate-300';
            const label = val === 0 ? 'Non intégré' : val === 1 ? "Début d'acquisition" : val === 2 ? 'En cours' : val === 3 ? 'Acquis' : 'Non évalué';
            const handleCellClick = () => {
              if (val !== undefined && val !== null) {
                toast.info(`${val} = ${label}`, { description: p.label, duration: 2000 });
              }
            };
            return (
              <td key={p.id} onClick={handleCellClick} className={`p-2 border border-slate-200 text-center text-xs font-bold cursor-pointer select-none ${color}`}>
                {val !== undefined && val !== null ? val : '-'}
              </td>
            );
          })}
        </tr>
      );
    })}
  </>
);

export default GrilleEvaluationCentreSection;
